import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAnalytics } from "../../lib/api";
import { writePrefs, readPrefs } from "../../lib/prefs";

/**
 * Known monogram SVG logos hand-shipped from /public/assets/games/.
 * Keyed by the game's canonical slug so we can look up by either the
 * server-provided `slug` field OR a normalised name. Games not in the
 * map fall back to an initials monogram tile so the strip still
 * renders 12 entries even when only 6 logos exist on disk.
 */
const KNOWN_LOGOS = {
  "soccer-irl": "/assets/games/soccer.svg",
  "valorant": "/assets/games/valorant.svg",
  "league-of-legends": "/assets/games/lol.svg",
  "rocket-league": "/assets/games/rocket-league.svg",
  "cs2": "/assets/games/cs2.svg",
  "counter-strike-2": "/assets/games/cs2.svg",
  "ea-fc": "/assets/games/fifa.svg",
  "fifa": "/assets/games/fifa.svg",
};

// Fallback list used when /api/analytics is unreachable. Mirrors the
// previously-hardcoded six games so the landing never appears empty.
const FALLBACK_GAMES = [
  { name: "Soccer (IRL)",       slug: "soccer-irl" },
  { name: "Valorant",           slug: "valorant" },
  { name: "League of Legends",  slug: "league-of-legends" },
  { name: "Rocket League",      slug: "rocket-league" },
  { name: "Counter-Strike 2",   slug: "cs2" },
  { name: "EA FC 25 (FIFA)",    slug: "ea-fc" },
];

function initials(name) {
  // Two-letter monogram. Strip punctuation, take first letter of
  // first two whitespace-split words. e.g. "Rocket League" -> "RL".
  const words = String(name)
    .replace(/[^A-Za-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "GG";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function testidFor(name) {
  return `hero-game-${String(name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`;
}

/**
 * GamePickStrip — top-12 games by player count.
 *
 * Pulls the live `by_game` list from `/api/analytics` (server-side
 * sorted by popularity), slices the top 12, and renders a horizontal
 * scrollable strip. Each tile:
 *   - persists `last_game` to localStorage via writePrefs
 *   - navigates to `/leagues?game=<game name>` on click
 *
 * If analytics is unreachable the strip falls back to a small
 * static list so the landing never renders an empty container.
 */
export function GamePickStrip() {
  const nav = useNavigate();
  const [games, setGames] = useState(FALLBACK_GAMES);

  useEffect(() => {
    let cancelled = false;
    fetchAnalytics()
      .then((d) => {
        if (cancelled) return;
        const list = Array.isArray(d?.by_game) ? d.by_game : [];
        if (!list.length) return;
        // backend already sorts by popularity desc; take top 12.
        const top = list.slice(0, 12).map((row) => ({
          name: row.game,
          slug: row.slug || null,
          players: row.players || 0,
        }));
        setGames(top);
      })
      .catch(() => {
        // keep FALLBACK_GAMES already in state.
      });
    return () => { cancelled = true; };
  }, []);

  const prev = readPrefs().last_game;
  const pick = (g) => {
    writePrefs({ last_game: g.name });
    nav(`/leagues?game=${encodeURIComponent(g.name)}`);
  };

  return (
    <div data-testid="hero-game-strip" className="mt-12">
      <p className="text-center text-[13px] text-zinc-500 mb-4 font-normal">
        What do you play?
      </p>
      <div
        className="flex flex-wrap justify-center gap-[10px] max-w-5xl mx-auto"
      >
        {games.map((g) => {
          const selected = prev === g.name;
          const logo = g.slug ? KNOWN_LOGOS[g.slug] : null;
          return (
            <button
              key={g.slug || g.name}
              type="button"
              onClick={() => pick(g)}
              data-testid={testidFor(g.name)}
              title={g.name}
              className={[
                "shrink-0 inline-flex items-center gap-[10px]",
                "h-12 px-4 rounded-full text-[13px] font-medium transition-colors",
                selected
                  ? "border border-[#14b8a6] bg-[#14b8a6]/[0.12] text-white"
                  : "border border-white/10 bg-transparent text-zinc-300 hover:border-white/25 hover:bg-white/[0.04]",
              ].join(" ")}
            >
              {logo ? (
                <img
                  src={logo}
                  alt=""
                  width={28}
                  height={28}
                  className="block"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/15 text-[10px] font-display grad-text"
                >
                  {initials(g.name)}
                </span>
              )}
              <span className="whitespace-nowrap">{g.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
